import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { formResolver } from "../../lib/formResolver";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { authApi } from "../../api/api";
import { signupSchema, SignupFormData } from "../../schemas/signup.schema";
import AuthLayout from "../../layouts/AuthLayout";
import { FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight, FiBriefcase, FiUser, FiPhone } from "react-icons/fi";

interface PlanOption {
  slug: string;
  name: string;
}

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPlan = searchParams.get("plan") || "starter";

  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [formError, setFormError] = useState<string>("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmittingApi, setIsSubmittingApi] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;
    // Plans are a public endpoint; failure here should not block manual entry.
    // Note: authApi is mounted at /api (no /v1), hence the explicit prefix.
    authApi
      .get("/v1/plans")
      .then((res) => {
        if (!isMounted) return;
        const data = res.data?.data;
        const plansData = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        setPlans(
          plansData
            .filter((plan: any) => plan?.slug)
            .map((plan: any) => ({ slug: plan.slug, name: plan.name || plan.slug }))
        );
      })
      .catch(() => {
        /* non-fatal */
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: formResolver(signupSchema),
    defaultValues: useMemo(
      () => ({
        company_name: "",
        admin_name: "",
        email: "",
        phone: "",
        plan_slug: preselectedPlan,
        password: "",
        confirmPassword: "",
      }),
      [preselectedPlan]
    ),
  });

  const applyServerFieldErrors = (errorsObject: Record<string, any>) => {
    const mapped: Record<string, string> = {};
    Object.entries(errorsObject || {}).forEach(([key, value]) => {
      mapped[key] = Array.isArray(value) ? String(value[0]) : String(value);
    });
    setFieldErrors(mapped);
  };

  const onSubmit = async (data: SignupFormData) => {
    try {
      setFormError("");
      setFieldErrors({});
      setIsSubmittingApi(true);
      await authApi.post("/register-company", {
        company_name: data.company_name,
        admin_name: data.admin_name,
        email: data.email,
        phone: data.phone || undefined,
        plan_slug: data.plan_slug,
        password: data.password,
        password_confirmation: data.confirmPassword,
      });
      navigate("/login", {
        state: {
          signupSuccess: `Your organization "${data.company_name}" is ready. Your 14-day free trial has started — sign in to continue.`,
        },
      });
    } catch (err: any) {
      const payload = err?.response?.data;
      if (payload?.errors && typeof payload.errors === "object") {
        applyServerFieldErrors(payload.errors);
        setFormError(payload.message || "Please fix the highlighted fields and try again.");
      } else {
        setFormError(payload?.message || err?.message || "Registration failed. Please try again.");
      }
    } finally {
      setIsSubmittingApi(false);
    }
  };

  const planOptions = plans.length > 0 ? plans : [{ slug: preselectedPlan, name: preselectedPlan }];
  const fieldError = (field: keyof SignupFormData): string | undefined =>
    errors[field]?.message || fieldErrors[field];

  return (
    <AuthLayout>
      <div className="w-full bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200/80 grid grid-cols-1 lg:grid-cols-12">
        {/* Left Side: Brand Panel */}
        <div className="lg:col-span-5 bg-gradient-to-br from-[#064e3b] via-[#043e2e] to-[#022c22] p-8 sm:p-10 flex flex-col justify-between text-white">
          <div className="space-y-6">
            <Link to="/" className="flex items-center gap-3 w-fit">
              <div className="h-11 w-11 rounded-2xl bg-white/10 border border-white/20 backdrop-blur-md flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#34d399" />
                  <path d="M2 17L12 22L22 17" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M2 12L12 17L22 12" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="font-black text-xl tracking-tight block leading-tight text-white">
                Strux<span className="text-emerald-400">CMS</span>
              </span>
            </Link>

            <div className="space-y-3 pt-4">
              <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight">
                Create your organization workspace
              </h1>
              <p className="text-xs text-emerald-100/75 leading-relaxed">
                Register your company with a 14-day free trial — no credit card required. You'll be the
                administrator of your organization's portal from day one.
              </p>
            </div>

            <ul className="space-y-2 pt-2">
              {["Full suite access during trial", "Projects, billing & payroll modules", "Cancel or switch plans anytime"].map(
                (item) => (
                  <li key={item} className="flex items-center gap-2 text-[11px] font-semibold text-emerald-100/80">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    {item}
                  </li>
                )
              )}
            </ul>
          </div>

          <div className="pt-8 text-[11px] text-emerald-200/50 font-medium">
            © {new Date().getFullYear()} StruxCMS. All rights reserved.
          </div>
        </div>

        {/* Right Side: Form */}
        <div className="lg:col-span-7 p-8 sm:p-10 flex flex-col justify-center bg-white">
          <div className="max-w-md w-full mx-auto space-y-5">
            <div>
              <h2 className="text-xl font-black text-slate-900 tracking-tight">Start your free trial</h2>
              <p className="text-xs font-medium text-slate-500 mt-1">
                Already registered?{" "}
                <Link to="/login" className="font-bold text-[#064e3b] hover:text-[#047857]">
                  Sign in instead
                </Link>
              </p>
            </div>

            {/* Plan selector */}
            <div>
              <label className="block text-xs font-bold uppercase text-slate-500 mb-1.5 tracking-wider">
                Plan
              </label>
              <select
                {...register("plan_slug")}
                className={`w-full px-4 py-3 bg-slate-50/80 border rounded-xl text-xs font-semibold text-slate-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-emerald-600/10 transition-all ${
                  fieldError("plan_slug") ? "border-rose-400 bg-rose-50/30" : "border-slate-200/90"
                }`}
              >
                {planOptions.map((plan) => (
                  <option key={plan.slug} value={plan.slug}>
                    {plan.name}
                  </option>
                ))}
              </select>
              {(fieldError("plan_slug") || fieldErrors.plan_slug) && (
                <p className="text-[11px] font-bold text-rose-500 mt-1">{fieldError("plan_slug")}</p>
              )}
            </div>

            {formError && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-800 rounded-2xl text-xs font-semibold flex items-start gap-2">
                <span className="h-2 w-2 mt-1 rounded-full bg-rose-500 shrink-0" />
                <span>{formError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase text-slate-500 mb-1.5 tracking-wider">
                  Company Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <FiBriefcase className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    {...register("company_name")}
                    placeholder="Acme Constructions Pvt. Ltd."
                    disabled={isSubmittingApi}
                    className={`w-full pl-10 pr-4 py-3 bg-slate-50/80 border rounded-xl text-xs font-semibold text-slate-900 placeholder:text-slate-400 placeholder:font-normal focus:outline-none focus:bg-white focus:ring-2 focus:ring-emerald-600/10 transition-all ${
                      fieldError("company_name") ? "border-rose-400 bg-rose-50/30" : "border-slate-200/90"
                    }`}
                  />
                </div>
                {fieldError("company_name") && (
                  <p className="text-[11px] font-bold text-rose-500 mt-1">{fieldError("company_name")}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-slate-500 mb-1.5 tracking-wider">
                    Admin Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <FiUser className="w-4 h-4" />
                    </div>
                    <input
                      type="text"
                      {...register("admin_name")}
                      placeholder="Full name"
                      disabled={isSubmittingApi}
                      className={`w-full pl-10 pr-4 py-3 bg-slate-50/80 border rounded-xl text-xs font-semibold text-slate-900 placeholder:text-slate-400 placeholder:font-normal focus:outline-none focus:bg-white focus:ring-2 focus:ring-emerald-600/10 transition-all ${
                        fieldError("admin_name") ? "border-rose-400 bg-rose-50/30" : "border-slate-200/90"
                      }`}
                    />
                  </div>
                  {fieldError("admin_name") && (
                    <p className="text-[11px] font-bold text-rose-500 mt-1">{fieldError("admin_name")}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase text-slate-500 mb-1.5 tracking-wider">
                    Phone (optional)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <FiPhone className="w-4 h-4" />
                    </div>
                    <input
                      type="tel"
                      {...register("phone")}
                      placeholder="+91 ..."
                      disabled={isSubmittingApi}
                      className={`w-full pl-10 pr-4 py-3 bg-slate-50/80 border rounded-xl text-xs font-semibold text-slate-900 placeholder:text-slate-400 placeholder:font-normal focus:outline-none focus:bg-white focus:ring-2 focus:ring-emerald-600/10 transition-all ${
                        fieldError("phone") ? "border-rose-400 bg-rose-50/30" : "border-slate-200/90"
                      }`}
                    />
                  </div>
                  {fieldError("phone") && (
                    <p className="text-[11px] font-bold text-rose-500 mt-1">{fieldError("phone")}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-slate-500 mb-1.5 tracking-wider">
                  Admin Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <FiMail className="w-4 h-4" />
                  </div>
                  <input
                    type="email"
                    {...register("email")}
                    placeholder="name@company.com"
                    disabled={isSubmittingApi}
                    className={`w-full pl-10 pr-4 py-3 bg-slate-50/80 border rounded-xl text-xs font-semibold text-slate-900 placeholder:text-slate-400 placeholder:font-normal focus:outline-none focus:bg-white focus:ring-2 focus:ring-emerald-600/10 transition-all ${
                      fieldError("email") ? "border-rose-400 bg-rose-50/30" : "border-slate-200/90"
                    }`}
                  />
                </div>
                {fieldError("email") && (
                  <p className="text-[11px] font-bold text-rose-500 mt-1">{fieldError("email")}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-slate-500 mb-1.5 tracking-wider">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <FiLock className="w-4 h-4" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      {...register("password")}
                      placeholder="Min. 8 characters"
                      disabled={isSubmittingApi}
                      className={`w-full pl-10 pr-10 py-3 bg-slate-50/80 border rounded-xl text-xs font-semibold text-slate-900 placeholder:text-slate-400 placeholder:font-normal focus:outline-none focus:bg-white focus:ring-2 focus:ring-emerald-600/10 transition-all ${
                        fieldError("password") ? "border-rose-400 bg-rose-50/30" : "border-slate-200/90"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                    >
                      {showPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                    </button>
                  </div>
                  {fieldError("password") && (
                    <p className="text-[11px] font-bold text-rose-500 mt-1">{fieldError("password")}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase text-slate-500 mb-1.5 tracking-wider">
                    Confirm Password
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    {...register("confirmPassword")}
                    placeholder="Repeat password"
                    disabled={isSubmittingApi}
                    className={`w-full px-4 py-3 bg-slate-50/80 border rounded-xl text-xs font-semibold text-slate-900 placeholder:text-slate-400 placeholder:font-normal focus:outline-none focus:bg-white focus:ring-2 focus:ring-emerald-600/10 transition-all ${
                      fieldError("confirmPassword") ? "border-rose-400 bg-rose-50/30" : "border-slate-200/90"
                    }`}
                  />
                  {fieldError("confirmPassword") && (
                    <p className="text-[11px] font-bold text-rose-500 mt-1">{fieldError("confirmPassword")}</p>
                  )}
                </div>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmittingApi}
                  className="w-full py-3.5 bg-[#064e3b] hover:bg-[#047857] text-white font-extrabold text-xs rounded-xl shadow-md shadow-emerald-950/20 transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-70 cursor-pointer"
                >
                  {isSubmittingApi ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Creating workspace...
                    </>
                  ) : (
                    <>
                      Start free trial <FiArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>

            <p className="text-center text-[11px] text-slate-400 font-medium">
              By registering you agree to our terms of service and privacy policy.
            </p>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Signup;
